import { GetPayeeEngagementAsPayerExpandParameterInner } from "@wingspanhq/wingspan-api-sdk";
import {
  WSActions,
  WSCard,
  WSForm,
  WSList,
  WSSectionToolbar
} from "@wingspanhq/fe-component-library";
import { InvoiceStatus } from "@wingspanhq/payments/dist/interfaces";
import { RouteComponentProps, useHistory } from "react-router-dom";
import { INVOICING_PATH } from "../..";
import { ErrorAlert } from "@components/ErrorAlert";
import {
  queriesInvoicingConfigs,
  queriesPayeeEngagements,
  queriesPayerRows,
  queriesWorkItems,
  queriesWorkLogs
} from "@query";
import { useQueryInvoice } from "../../../../query/payments/queries";
import { Layout } from "../../../../shared/components/Layout";
import { CardWorkItems } from "../../../WorkLogs/components/CardWorkItems";
import { activeWorkItemsFilter } from "../../../WorkLogs/utils/activeWorkItemsFilter";
import { WorkLogEngagementDetails } from "../../../WorkLogs/components/WorkLogEngagementDetails";
import { AdditionalDetails } from "./AdditionalDetails";
import { useMutationSubmit } from "./useMutationSubmit";
import { selectorIsPayeeAllowedToInitiateInvoice } from "../../../../Invoices/selectors/selectorIsPayeeAllowedToInitiateInvoice";
import { AlertClientNotAllowedSubmission } from "../../../../Invoices/components/AlertClientNotAllowedSubmission";
import React from "react";
import { useUserId } from "../../../../query/hooks/helpers";

export type FormDataAttachment = {
  fileId: string;
  remove?: string;
};

export type FormDataWorkLogInvoice = {
  notes: string;
  attachments: FormDataAttachment[];
};

export const RouteEditWorkLogInvoice: React.FC<
  RouteComponentProps<{ invoiceId: string }>
> = props => {
  const userId = useUserId();
  const history = useHistory();
  const invoiceId = props.match.params.invoiceId;

  const queryInvoice = useQueryInvoice(invoiceId);
  const queryWorkItem = queriesWorkItems.getWorkItem.useQuery(
    {
      workItemId:
        (queryInvoice.data?.lineItems[0].labels?.workItemId as string) ?? ""
    },
    {
      enabled: !!queryInvoice.data?.lineItems[0].labels.workItemId
    }
  );

  const workLogId = queryWorkItem.data?.workLogId;

  // Resolve the v3 engagement from the work log linked to this invoice.
  const queryWorkLog = queriesWorkLogs.getWorkLog.useQuery(
    { workLogId: workLogId! },
    { enabled: !!workLogId }
  );
  const workLog = queryWorkLog.data;

  const queryPayerRow = queriesPayerRows.searchPayerRows.useQuery(
    {
      filter: {
        engagements_id: {
          eq: workLog?.payeeEngagementId ?? undefined
        }
      }
    },
    { enabled: !!workLog?.payeeEngagementId }
  );

  const payerRow = queryPayerRow.data?.data?.[0];
  const payerId = payerRow?.id;
  const payerPayeeEngagementId = workLog?.payeeEngagementId ?? undefined;

  const queryPayerPayeeEngagement =
    queriesPayeeEngagements.getPayeeEngagementAsPayer.useQuery(
      {
        payeeEngagementId: payerPayeeEngagementId!,
        expand: new Set([
          GetPayeeEngagementAsPayerExpandParameterInner.WORK_DEFINITIONS
        ])
      },
      { enabled: !!payerPayeeEngagementId }
    );

  // `workLogId` is read off the work item above, so it is absent on first
  // render, and `{ eq: undefined }` serializes to an empty `filter[workLogId]`
  // — the list ran unconstrained and rendered other work logs' items until it
  // resolved.
  const queryWorkItems = queriesWorkItems.listWorkItems.useQuery(
    { filter: activeWorkItemsFilter(workLogId!) },
    { enabled: !!workLogId }
  );

  // Keyed on the payer's *account* id, not the route's `payerId` — the latter is
  // `Payer.id`, the composite `<payerAccountId>_<payeeAccountId>` of the link.
  const payerAccountId = payerRow?.payerAccountId;
  const queryInvoicingConfigForPayee =
    queriesInvoicingConfigs.getInvoicingConfigForPayee.useQuery(
      { payerAccountId: payerAccountId as string },
      { enabled: !!payerAccountId, retry: false }
    );

  const invoice = queryInvoice.data;
  const payerPayeeEngagement = queryPayerPayeeEngagement.data;
  const workDefinitions = queryPayerPayeeEngagement.data?.workDefinitions ?? [];
  const workItems = queryWorkItems.data?.data || [];
  const isPayeeNotAllowedToInitiateInvoice =
    !selectorIsPayeeAllowedToInitiateInvoice(
      queryInvoicingConfigForPayee.data,
      userId
    );

  const [save, saveMeta] = useMutationSubmit(invoiceId);
  const [send, sendMeta] = useMutationSubmit(invoiceId, {
    sendInvoice: true
  });

  if (!invoice || !payerRow || !payerPayeeEngagement) {
    return (
      <Layout title="Edit invoice" wide>
        <WSCard shimmer>
          <WSSectionToolbar title="Assignment" />
        </WSCard>
      </Layout>
    );
  }

  return (
    <Layout
      title="Edit invoice"
      wide
      onClose={() => {
        history.push(INVOICING_PATH);
      }}
    >
      <WSForm<FormDataWorkLogInvoice>
        defaultValues={{
          notes: invoice.invoiceNotes || "",
          attachments:
            invoice.attachments?.customAttachmentIds?.map(id => ({
              fileId: id
            })) || []
        }}
      >
        <WSList gap="2XL">
          <WorkLogEngagementDetails
            payerRow={payerRow}
            payerPayeeEngagement={payerPayeeEngagement}
          />
          {isPayeeNotAllowedToInitiateInvoice && (
            <AlertClientNotAllowedSubmission />
          )}

          <CardWorkItems
            shimmer={
              queryWorkItems.isInitialLoading ||
              queryPayerPayeeEngagement.isInitialLoading
            }
            noEdit
            workLogId={workLogId!}
            payerId={payerId!}
            payerPayeeEngagementId={payerPayeeEngagementId!}
            workItems={workItems}
            workDefinitions={workDefinitions}
            resources={{
              payerPayeeEngagement
            }}
          />

          <AdditionalDetails notesInitialVisible={!!invoice.invoiceNotes} />

          <ErrorAlert
            context="InvoiceForm"
            error={saveMeta.error || sendMeta.error}
          />

          <WSForm.Context>
            {context => (
              <WSActions
                alignment="fill"
                disabled={isPayeeNotAllowedToInitiateInvoice}
                buttons={
                  invoice.status === InvoiceStatus.Open
                    ? [
                        {
                          label: "Save invoice",
                          loading: saveMeta.isLoading,
                          type: "button",
                          onClick: () => {
                            context.handleSubmit(values => {
                              save(values as FormDataWorkLogInvoice);
                            })();
                          }
                        }
                      ]
                    : [
                        {
                          kind: "Tertiary",
                          label: "Save draft and exit",
                          loading: saveMeta.isLoading,
                          type: "button",
                          onClick: () => {
                            context.handleSubmit(values => {
                              save(values as FormDataWorkLogInvoice);
                            })();
                          }
                        },
                        {
                          label: "Send invoice",
                          loading: sendMeta.isLoading,
                          type: "button",
                          onClick: () => {
                            context.handleSubmit(values => {
                              send(values as FormDataWorkLogInvoice);
                            })();
                          }
                        }
                      ]
                }
              />
            )}
          </WSForm.Context>
        </WSList>
      </WSForm>
    </Layout>
  );
};
